import React, { useCallback } from 'react';
import { IOnSubmitArgs, OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { notification } from 'antd';
import { changePassword } from 'store/slices/authSlice';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';

interface InfoContentProps {
    form: any;
    onClose: () => void;
}

const CombinedFormContent: React.FC<InfoContentProps> = ({ form, onClose }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { globalUser } = useSelector((state: RootState) => state.users);
    const { t } = useTranslation();

    const handleSubmit = useCallback(async ({ values }: IOnSubmitArgs<any>) => {
        if (!!globalUser?.masterIntegrationId) {
            notification.error({
                message: t('notification.error'),
                description: t('profile.notification.editErrorExternalSystem'),
                placement: 'bottomRight',
            });
            return;
        }

        if (globalUser?.id) {
            const { currentPassword, newPassword } = values;
            try {
                await dispatch(changePassword({
                    orgId: orgId,
                    userId: globalUser.id,
                    payload: { currentPassword, newPassword }
                })).unwrap();
                notification.success({
                    message: t('notification.success'),
                    description: t('profile.password.success'),
                    placement: 'bottomRight',
                });
                onClose();
            } catch (error: any) {
                notification.error({
                    message: t('notification.error'),
                    description: error.message || t('profile.password.error'),
                    placement: 'bottomRight',
                });
            }
        }
    }, [dispatch, orgId, globalUser, onClose, t]);

    return (
        <OpForm
            form={form}
            onSubmit={handleSubmit}
            hasError={false}
            disabled={!!globalUser?.masterIntegrationId}
        >
            <OpForm.Item
                label={t('profile.password.current')}
                name='currentPassword'
                rules={!globalUser?.masterIntegrationId ? [
                    { required: true, message: t('profile.password.currentRequired') }
                ] : []}>
                <Input.Password
                    placeholder={t('profile.password.placeholderCurrent')}
                    style={{ width: '100%' }}
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
            </OpForm.Item>

            <OpForm.Item
                label={t('profile.password.new')}
                name='newPassword'
                rules={!globalUser?.masterIntegrationId ? [
                    { required: true, message: t('profile.password.newRequired') },
                    { min: 6, message: t('profile.password.newMin') }
                ] : []}
            >
                <Input.Password
                    placeholder={t('profile.password.placeholderNew')}
                    style={{ width: '100%' }}
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
            </OpForm.Item>
        </OpForm>
    );
};

export default CombinedFormContent;
